import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { searchProfiles, saveCandidate, unsaveCandidate, getRecruiterProjects, type SearchResult, type SearchFilters } from '@/lib/search';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Search, 
  MapPin, 
  Globe, 
  Lock, 
  Users, 
  LayoutGrid, 
  List,
  Building,
  Briefcase,
  BookmarkPlus,
  CheckCircle,
  X,
  Filter
} from 'lucide-react';

export default function SearchPage() {
  const { user, hasRecruiterLicense, hasLicense } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    location: '',
    skills: [],
    experienceLevel: 'any',
    educationLevel: 'any'
  });
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<SearchResult | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  useEffect(() => {
    performSearch();
    if (hasRecruiterLicense && user) {
      loadProjects();
    }
  }, [hasRecruiterLicense, user, filters]);

  const loadProjects = async () => {
    if (!user) return;
    const projectsData = await getRecruiterProjects(user.id);
    setProjects(projectsData);
  };

  const performSearch = async () => {
    setLoading(true);
    try {
      const searchResults = await searchProfiles(
        searchQuery.trim() || null,
        filters,
        hasRecruiterLicense,
        hasLicense('pro'),
        user?.id
      );
      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCandidate = async (projectId: string) => {
    if (!user || !selectedCandidate) return;
    
    try {
      const success = await saveCandidate(selectedCandidate.id, projectId, user.id);
      
      if (success) {
        setResults(results.map(result => 
          result.id === selectedCandidate.id
            ? {
                ...result,
                saved_to_projects: [...(result.saved_to_projects || []), projectId]
              }
            : result
        ));
        toast({ title: 'Candidate saved to project' });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save candidate'
      });
    } finally {
      setSaveDialogOpen(false);
      setSelectedCandidate(null);
    }
  };

  const handleUnsaveCandidate = async (candidateId: string, projectId: string) => {
    if (!user) return;
    
    try {
      const success = await unsaveCandidate(candidateId, projectId, user.id);
      if (success) {
        setResults(results.map(result => 
          result.id === candidateId
            ? {
                ...result,
                saved_to_projects: result.saved_to_projects?.filter(id => id !== projectId) || []
              }
            : result
        ));
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to remove candidate'
      });
    }
  };

  const renderProfile = (profile: SearchResult) => {
    const savedProjects = profile.saved_to_projects || [];
    
    return (
      <Card 
        key={profile.id} 
        className="hover:bg-muted/50 transition-colors cursor-pointer"
        onClick={() => navigate(`/profile/${profile.id}`, { state: { fromSearch: true } })}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12 border">
              <AvatarImage src={profile.avatar_url || ''} alt={profile.full_name} />
              <AvatarFallback>{profile.full_name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-medium truncate">{profile.full_name}</h3>
                  {profile.privacy_level === 'public' ? (
                    <Globe className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : profile.privacy_level === 'connections' ? (
                    <Users className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  ) : (
                    <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                </div>
                
                {hasRecruiterLicense && (
                  <div className="flex items-center gap-2 ml-4">
                    {savedProjects.length === 0 ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCandidate(profile);
                          setSaveDialogOpen(true);
                        }}
                      >
                        <BookmarkPlus className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                    ) : (
                      savedProjects.map(projectId => {
                        const project = projects.find(p => p.id === projectId);
                        if (!project) return null;
                        return (
                          <Badge key={projectId} variant="secondary" className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            {project.title}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnsaveCandidate(profile.id, projectId);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                <span className="truncate">{profile.title}</span>
                {profile.location && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3" />
                    {profile.location}
                  </span>
                )}
              </div>

              {profile.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {profile.skills.slice(0, 3).map((skill, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                  {profile.skills.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{profile.skills.length - 3}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Talent Search</h1>
            <p className="text-muted-foreground">Find and connect with professionals</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="icon"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, title, or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') performSearch(); }}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
            <Button onClick={performSearch}>Search</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        ) : results.length > 0 ? (
          <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
            {results.map(renderProfile)}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium">No Results Found</h3>
                <p className="text-muted-foreground">Try adjusting your search criteria</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Save Candidate Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Candidate</DialogTitle>
            <DialogDescription>Select a project to save this candidate to</DialogDescription>
          </DialogHeader>

          {projects.length > 0 ? (
            <div className="space-y-2">
              {projects.map(project => (
                <Card key={project.id} className="cursor-pointer hover:bg-muted/50">
                  <CardContent 
                    className="p-4 flex items-center justify-between"
                    onClick={() => handleSaveCandidate(project.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span>{project.title}</span>
                    </div>
                    <BookmarkPlus className="h-4 w-4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground">No active projects found</p>
              <Button 
                variant="outline" 
                className="mt-2"
                onClick={() => navigate('/projects')}
              >
                Create Project
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}